<template>
  <div
    v-if="visible"
    class="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto px-3 py-4 sm:items-center sm:px-4 sm:py-6"
  >
    <div class="absolute inset-0 bg-[#140f23]/55 backdrop-blur-sm"></div>

    <div
      class="relative z-10 my-auto max-h-[calc(100dvh-2rem)] w-full max-w-2xl overflow-hidden overflow-y-auto overscroll-contain rounded-3xl border border-white/70 bg-white shadow-[0_30px_90px_-32px_rgba(27,14,48,0.45)]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
    >
      <div
        class="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[radial-gradient(circle_at_35%_35%,rgba(255,42,166,0.22),transparent_62%)] blur-3xl"
      ></div>
      <div
        class="absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-[radial-gradient(circle_at_40%_40%,rgba(116,42,255,0.14),transparent_65%)] blur-3xl"
      ></div>

      <div
        class="relative border-b border-gray-100 bg-gradient-to-r from-[#f7f2ff] via-[#fff7fb] to-[#fffaf4] px-6 py-6 sm:px-8"
      >
        <div
          class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
        >
          <div class="self-end sm:order-2 sm:self-start sm:pt-1">
            <LanguageSwitcher variant="light" menu-placement="bottom" />
          </div>

          <div class="max-w-xl sm:order-1">
            <p
              class="text-xs font-semibold uppercase tracking-[0.24em] text-[#b0006b]"
            >
              {{ $t("privacyPreferences.eyebrow") }}
            </p>
            <h2
              :id="titleId"
              class="mt-3 text-2xl font-extrabold tracking-tight text-[#2e234b] sm:text-3xl"
            >
              {{ $t("privacyPreferences.title") }}
            </h2>
            <p class="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">
              {{ $t("privacyPreferences.description") }}
            </p>
            <p class="mt-2 text-sm text-gray-600">
              <i18n-t keypath="privacyPreferences.learnMore" tag="span">
                <template #link>
                  <NuxtLink
                    :to="localePath('/privacy')"
                    class="font-semibold text-[#2e234b] decoration-pink-300 underline-offset-4 hover:underline"
                  >
                    {{ $t("cookie.privacyPolicy") }}
                  </NuxtLink>
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
      </div>

      <div class="relative space-y-3 px-5 py-5 sm:px-8 sm:py-7">
        <div
          class="rounded-[28px] border border-[#ede6fb] bg-[linear-gradient(135deg,#f8f4ff_0%,#fffaf6_100%)] px-4 py-4 shadow-[0_16px_42px_-32px_rgba(44,24,84,0.35)] sm:px-5 sm:py-5"
        >
          <div
            class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div class="flex items-start gap-4">
              <span
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-[#2e234b] shadow-[0_10px_22px_rgba(46,35,75,0.12)]"
              >
                <svg
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </span>
              <div>
                <h3 class="text-sm font-semibold text-[#2e234b] sm:text-base">
                  {{ $t("privacyPreferences.necessaryTitle") }}
                </h3>
                <p class="mt-1 text-sm leading-relaxed text-gray-600">
                  {{ $t("privacyPreferences.necessaryDescription") }}
                </p>
              </div>
            </div>
            <span
              class="shrink-0 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#2e234b] shadow-[0_8px_20px_rgba(46,35,75,0.08)] ring-1 ring-[#eadffb]"
            >
              {{ $t("privacyPreferences.alwaysActive") }}
            </span>
          </div>
        </div>

        <label
          :class="optionCardClass(analyticsEnabled)"
          class="group relative flex cursor-pointer flex-col gap-4 overflow-hidden rounded-[28px] border px-4 py-4 transition-all duration-200 sm:flex-row sm:items-start sm:gap-4 sm:px-5 sm:py-5"
        >
          <input
            type="checkbox"
            class="sr-only"
            :checked="analyticsEnabled"
            @change="
              emit(
                'update:analyticsEnabled',
                ($event.target as HTMLInputElement).checked,
              )
            "
          />
          <div class="flex min-w-0 items-start gap-4">
            <span
              :class="optionIconClass(analyticsEnabled)"
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-all duration-200"
            >
              <svg
                class="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 18h16M7 14l3-3 3 2 4-5"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M7 18V9m6 9V7m4 11v-8"
                />
              </svg>
            </span>

            <div class="min-w-0 flex-1 sm:pr-2">
              <div>
                <h3 class="text-sm font-semibold text-[#2e234b] sm:text-base">
                  {{ $t("privacyPreferences.analyticsTitle") }}
                </h3>
                <p class="mt-1 text-sm leading-relaxed text-gray-600">
                  {{ $t("privacyPreferences.analyticsDescription") }}
                </p>
              </div>
            </div>
          </div>
          <span
            :class="toggleTrackClass(analyticsEnabled)"
            class="flex h-7 w-12 shrink-0 self-end rounded-full p-1 transition-all duration-200 sm:mt-1 sm:self-auto"
          >
            <span
              :class="toggleKnobClass(analyticsEnabled)"
              class="h-5 w-5 rounded-full bg-white shadow-[0_4px_10px_rgba(46,35,75,0.22)] transition-all duration-200"
            ></span>
          </span>
        </label>

        <label
          :class="optionCardClass(locationEnabled)"
          class="group relative flex cursor-pointer flex-col gap-4 overflow-hidden rounded-[28px] border px-4 py-4 transition-all duration-200 sm:flex-row sm:items-start sm:gap-4 sm:px-5 sm:py-5"
        >
          <input
            type="checkbox"
            class="sr-only"
            :checked="locationEnabled"
            @change="
              emit(
                'update:locationEnabled',
                ($event.target as HTMLInputElement).checked,
              )
            "
          />
          <div class="flex min-w-0 items-start gap-4">
            <span
              :class="optionIconClass(locationEnabled)"
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-all duration-200"
            >
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
                />
              </svg>
            </span>

            <div class="min-w-0 flex-1 sm:pr-2">
              <h3 class="text-sm font-semibold text-[#2e234b] sm:text-base">
                {{ $t("privacyPreferences.locationTitle") }}
              </h3>
              <p class="mt-1 text-sm leading-relaxed text-gray-600">
                {{ $t("privacyPreferences.locationDescription") }}
              </p>
            </div>
          </div>
          <span
            :class="toggleTrackClass(locationEnabled)"
            class="flex h-7 w-12 shrink-0 self-end rounded-full p-1 transition-all duration-200 sm:mt-1 sm:self-auto"
          >
            <span
              :class="toggleKnobClass(locationEnabled)"
              class="h-5 w-5 rounded-full bg-white shadow-[0_4px_10px_rgba(46,35,75,0.22)] transition-all duration-200"
            ></span>
          </span>
        </label>
      </div>

      <div class="border-t border-gray-100 bg-white px-5 py-5 sm:px-8">
        <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            class="h-12 rounded-2xl border border-gray-200 bg-white px-5 text-sm font-semibold text-[#2e234b] shadow-[0_10px_24px_rgba(46,35,75,0.08)] transition hover:bg-gray-50"
            @click="emit('onlyNecessary')"
          >
            {{ $t("privacyPreferences.onlyNecessary") }}
          </button>
          <button
            class="h-12 rounded-2xl bg-[#f5eefc] px-5 text-sm font-semibold text-[#2e234b] shadow-[0_10px_24px_rgba(46,35,75,0.08)] transition hover:bg-[#efe5fb]"
            @click="emit('save')"
          >
            {{ $t("privacyPreferences.saveSelection") }}
          </button>
          <button
            class="h-12 rounded-2xl bg-[linear-gradient(90deg,#3a0a6a_0%,#b0006b_100%)] px-5 text-sm font-semibold text-white shadow-[0_16px_34px_rgba(176,0,107,0.28)] transition hover:brightness-105"
            @click="emit('acceptAll')"
          >
            {{ $t("privacyPreferences.acceptAll") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  visible: boolean;
  analyticsEnabled: boolean;
  locationEnabled: boolean;
}>();

const emit = defineEmits<{
  (
    e: "update:analyticsEnabled" | "update:locationEnabled",
    value: boolean,
  ): void;
  (e: "onlyNecessary" | "save" | "acceptAll"): void;
}>();

const localePath = useLocalePath();
const titleId = "privacy-preferences-title";

function optionCardClass(enabled: boolean) {
  return enabled
    ? "border-[#f2c9e6] bg-[linear-gradient(135deg,#fff9fc_0%,#f7f1ff_100%)] shadow-[0_18px_50px_-28px_rgba(176,0,107,0.35)]"
    : "border-gray-200 bg-white shadow-[0_14px_34px_-28px_rgba(27,14,48,0.24)] hover:border-[#e4d7f6] hover:bg-[#fcfbff]";
}

function optionIconClass(enabled: boolean) {
  return enabled
    ? "bg-[#2e234b] text-white shadow-[0_14px_28px_rgba(46,35,75,0.18)]"
    : "bg-[#f4effb] text-[#2e234b]";
}

function toggleTrackClass(enabled: boolean) {
  return enabled ? "bg-[#2e234b]" : "bg-gray-200";
}

function toggleKnobClass(enabled: boolean) {
  return enabled ? "translate-x-5" : "translate-x-0";
}
</script>
